import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { ROUTE_FOR_STATUS } from '../utils/signup/route-for-application-status';
import { FeatureFlags } from '@square/dashboard-protos/models/squareup/sfs/external/models/feature_flags';
import { replace } from '@square/dashboard-utilities/utils/window';
import { SAVINGS_URL, DASHBOARD_URL } from '@banking/ui/utils/constants';
import { ReimaginedNavBankingVariations } from '../utils/launch-darkly-feature-flags';

export default class SignupRoute extends Route {
  @service sfsCustomerApi;
  @service currentApplicationFlow;
  @service errorHandler;
  @service features;
  @service router;
  @service sentry;

  async model() {
    const { customer, features } = await this.sfsCustomerApi.retrieveCustomer();

    this.setFeatures(features);

    this.checkAppSubdomain();

    // If the customer has an application flow, go ahead and fetch the whole thing now
    // so it will be cached in currentApplicationFlow for other signup routes.
    if (customer.applicationFlows.length > 0) {
      const { id } = customer.applicationFlows[0];
      await this.currentApplicationFlow.retrieve(id);
    }

    return customer;
  }

  redirect(customer) {
    if (
      this.features.getFlag('dashboard/reimagined_nav_banking_variations') ===
        ReimaginedNavBankingVariations.Test &&
      customer.applicationFlows.length === 0
    ) {
      this.router.transitionTo('loading');
      replace(SAVINGS_URL);
    } else if (!this.features.isEnabled('banking/access_sfs_savings')) {
      // This should prevent us briefly rendering the page before redirecting
      this.router.transitionTo('loading');
      replace(DASHBOARD_URL);
    } else if (customer.isLocked) {
      this.router.transitionTo('signup.locked');
    } else if (customer.primaryAccountId) {
      // This should prevent us briefly rendering the page before redirecting
      this.router.transitionTo('loading');
      replace(SAVINGS_URL);
    } else if (this.currentApplicationFlow.id) {
      // The currentApplicationFlow status may have been updated when it was retrieved
      // so redirect based on the status of the latest flow
      this.redirectFromApplicationStatus(this.currentApplicationFlow);
    } else {
      this.router.transitionTo('signup.index');
    }
  }

  redirectFromApplicationStatus({ status }) {
    const route = ROUTE_FOR_STATUS[status];
    if (route) {
      this.router.transitionTo(route);
    } else {
      throw new Error(`unexpected ApplicationFlow status: ${status}`);
    }
  }

  checkAppSubdomain() {
    const host = window.location.host;
    const includesAppSubdomain = host.startsWith('app.squareup');

    if (includesAppSubdomain || !host.startsWith('squareup')) {
      return;
    }

    const fullPath = window.location.pathname + window.location.search;
    const protocol = `${window.location.protocol}//`;

    const newURL = `${protocol}app.${host}${fullPath}`;
    window.location.replace(newURL);
  }

  // this method is called by child controllers of signup to share handling of rpc errors:
  // `options` can be used to override the error route in the event of a generic error
  @action handleRpcError(error, options = {}) {
    this.errorHandler.handleRpcError(error, options);
  }

  setFeatures(features) {
    const flagMap = {};
    for (const { name, value, intValue, valueType } of features) {
      switch (valueType) {
        case FeatureFlags.ValueType.VALUE_TYPE_INTEGER:
          flagMap[name] = intValue;
          break;
        case FeatureFlags.ValueType.VALUE_TYPE_BOOLEAN:
          flagMap[name] = value;
          break;
        default:
          this.sentry.captureException('Unhandled feature flag value type');
          flagMap[name] = value;
      }
    }

    this.features.setup(flagMap);
  }
}
